import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
    Star, Award, CheckCircle, TrendingUp, Building2, 
    Clock, Shield, Edit3, ArrowRight, BarChart2 
} from 'lucide-react';
import { Badge, Button, EmptyState, Modal, TextArea, toast } from '../../components/common';
import { rfqApi } from '../../services/api';
import './QuotationComparison.css';

export default function QuotationComparison({ rfqId, quotations: initialQuotations, items: rfqItems }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    // Modal state for scoring evaluation
    const [evalModalOpen, setEvalModalOpen] = useState(false);
    const [activeQuotation, setActiveQuotation] = useState(null);
    const [technicalScore, setTechnicalScore] = useState(80);
    const [commercialScore, setCommercialScore] = useState(80);
    const [deliveryScore, setDeliveryScore] = useState(80);
    const [evaluationNotes, setEvaluationNotes] = useState('');

    // Fetch live compare data (including scoring and rankings)
    const { data: compareData, isLoading, refetch } = useQuery({
        queryKey: ['rfq-compare', rfqId],
        queryFn: () => rfqApi.compareQuotations(rfqId),
        select: (res) => res.data?.data,
        enabled: !!rfqId
    });

    // Mutation to evaluate quotation
    const evaluateMutation = useMutation({
        mutationFn: (data) => rfqApi.evaluateQuotation(rfqId, activeQuotation.id, data),
        onSuccess: () => {
            toast.success('Hasil scoring berhasil disimpan dan diperbarui!');
            queryClient.invalidateQueries(['rfq-compare', rfqId]);
            queryClient.invalidateQueries(['rfq', rfqId]);
            refetch();
            setEvalModalOpen(false);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Gagal menyimpan scoring');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const quotations = compareData?.quotations || initialQuotations || [];

    if (!quotations || quotations.length === 0) {
        return (
            <div className="quotation-comparison-empty">
                <EmptyState
                    icon={Star}
                    title="Belum Ada Quotation Masuk"
                    description="Vendor yang diundang belum mengirimkan surat penawaran harga untuk RFQ ini."
                />
            </div>
        );
    }

    // Sort by overallScore descending, then by totalAmount ascending
    const rankedQuotations = [...quotations].sort((a, b) => {
        const scoreA = parseFloat(a.overallScore || 0);
        const scoreB = parseFloat(b.overallScore || 0);
        if (scoreB !== scoreA) return scoreB - scoreA;
        return parseFloat(a.totalAmount || 0) - parseFloat(b.totalAmount || 0);
    });

    const openEvaluationModal = (q) => {
        setActiveQuotation(q);
        setTechnicalScore(parseFloat(q.technicalScore || 80));
        setCommercialScore(parseFloat(q.commercialScore || 80));
        setDeliveryScore(parseFloat(q.deliveryScore || 80));
        setEvaluationNotes(q.evaluationNotes || '');
        setEvalModalOpen(true);
    };

    // Calculate live weighted score for modal
    const liveOverallScore = (
        (parseFloat(technicalScore || 0) * 0.4) +
        (parseFloat(commercialScore || 0) * 0.4) +
        (parseFloat(deliveryScore || 0) * 0.2)
    ).toFixed(2);

    const handleSaveEvaluation = (e) => {
        e.preventDefault();
        evaluateMutation.mutate({
            technicalScore: parseFloat(technicalScore),
            commercialScore: parseFloat(commercialScore),
            deliveryScore: parseFloat(deliveryScore),
            evaluationNotes
        });
    };

    return (
        <div className="quotation-comparison">
            {/* Header Section */}
            <div className="comparison-header flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h4>
                        <BarChart2 size={20} className="text-blue-600" />
                        Hasil Scoring &amp; Evaluasi Penawaran Vendor
                    </h4>
                    <p>
                        Perbandingan komprehensif penawaran vendor dengan pembobotan standar: Teknis (40%), Komersial (40%), dan Delivery (20%).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant="secondary" 
                        icon={Edit3}
                        onClick={() => openEvaluationModal(rankedQuotations[0])}
                    >
                        Beri Nilai Scoring
                    </Button>
                    <Button 
                        icon={Award}
                        onClick={() => navigate(`/rfq/${rfqId}/award`)}
                    >
                        Tetapkan Pemenang
                    </Button>
                </div>
            </div>

            {/* 1. SCORING LEADERBOARD CARDS */}
            <div className="scoring-section">
                <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                        🏆 Leaderboard Hasil Scoring &amp; Rekomendasi Pemenang
                    </h5>
                    <span className="text-xs text-slate-500 font-medium">
                        Total {rankedQuotations.length} Penawaran Masuk
                    </span>
                </div>

                <div className="scoring-grid">
                    {rankedQuotations.map((q, idx) => {
                        const vendorName = q.vendor?.companyName || q.vendor?.name || 'Vendor ' + (idx + 1);
                        const vendorCode = q.vendor?.vendorCode || 'VND';
                        const overall = parseFloat(q.overallScore || 0);
                        const tech = parseFloat(q.technicalScore || 0);
                        const comm = parseFloat(q.commercialScore || 0);
                        const deliv = parseFloat(q.deliveryScore || (q.deliveryDays ? 80 : 0));
                        const isRank1 = idx === 0;

                        return (
                            <div 
                                key={q.id || idx} 
                                className={`scoring-card ${isRank1 ? 'rank-1' : ''}`}
                            >
                                <div className="scoring-card-header">
                                    <div className="scoring-vendor-info">
                                        <h4 className="scoring-vendor-title" title={vendorName}>
                                            {vendorName}
                                        </h4>
                                        <div className="scoring-vendor-meta">
                                            <span>{vendorCode}</span>
                                            <span>•</span>
                                            <span className="font-mono text-xs">{q.quotationNumber}</span>
                                        </div>
                                    </div>
                                    <div className={`scoring-rank-badge ${isRank1 ? 'rank-gold' : 'rank-silver'}`}>
                                        {isRank1 ? <Star size={14} fill="#b45309" color="#b45309" /> : null}
                                        <span>Rank #{idx + 1}</span>
                                    </div>
                                </div>

                                {/* Overall Score Box */}
                                <div className={`overall-score-banner ${isRank1 ? 'gold' : ''}`}>
                                    <div>
                                        <div className="overall-score-label">Skor Akhir (Overall)</div>
                                        <div className="text-xs text-slate-500">Bobot gabungan 100%</div>
                                    </div>
                                    <div className="overall-score-val">
                                        {overall > 0 ? overall.toFixed(1) : '—'}
                                        <span className="text-xs font-semibold text-slate-400">/100</span>
                                    </div>
                                </div>

                                {/* Score Breakdown */}
                                <div className="score-breakdown-list">
                                    <div className="score-breakdown-item">
                                        <div className="score-breakdown-head">
                                            <span>Teknis (Bobot 40%)</span>
                                            <span className="font-bold text-blue-600">{tech > 0 ? tech.toFixed(1) : '0'}</span>
                                        </div>
                                        <div className="score-progress-track">
                                            <div 
                                                className="score-progress-fill technical" 
                                                style={{ width: `${Math.min(100, tech)}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div className="score-breakdown-item">
                                        <div className="score-breakdown-head">
                                            <span>Komersial/Harga (Bobot 40%)</span>
                                            <span className="font-bold text-emerald-600">{comm > 0 ? comm.toFixed(1) : '0'}</span>
                                        </div>
                                        <div className="score-progress-track">
                                            <div 
                                                className="score-progress-fill commercial" 
                                                style={{ width: `${Math.min(100, comm)}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div className="score-breakdown-item">
                                        <div className="score-breakdown-head">
                                            <span>Pengiriman/SLA (Bobot 20%)</span>
                                            <span className="font-bold text-purple-600">{deliv > 0 ? deliv.toFixed(1) : '0'}</span>
                                        </div>
                                        <div className="score-progress-track">
                                            <div 
                                                className="score-progress-fill delivery" 
                                                style={{ width: `${Math.min(100, deliv)}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Price & Status */}
                                <div className="scoring-price-row">
                                    <div>
                                        <span className="text-xs text-slate-500 block">Total Penawaran</span>
                                        <span className="scoring-price-amount">
                                            {formatCurrency(q.totalAmount || q.total || 0)}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs text-slate-500 block">Status</span>
                                        <Badge 
                                            variant={q.status === 'awarded' ? 'success' : q.status === 'under_evaluation' ? 'warning' : 'info'}
                                            size="sm"
                                        >
                                            {q.status}
                                        </Badge>
                                    </div>
                                </div>

                                {/* Action button */}
                                <div className="scoring-card-actions">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        icon={Edit3}
                                        className="w-full justify-center"
                                        onClick={() => openEvaluationModal(q)}
                                    >
                                        Ubah / Input Scoring
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 2. DETAIL SIDE-BY-SIDE LINE ITEM TABLE */}
            <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                        📋 Matriks Perbandingan Detail Item Pengadaan
                    </h5>
                    <span className="text-xs text-slate-500">
                        {rfqItems?.length || compareData?.lineComparison?.length || 0} Item Terdaftar
                    </span>
                </div>

                <div className="comparison-table-wrapper shadow-xs">
                    <table className="comparison-table">
                        <thead>
                            <tr>
                                <th className="sticky-col">Item / Spesifikasi</th>
                                <th className="sticky-col qty-col">Qty</th>
                                {rankedQuotations.map((q) => {
                                    const vName = q.vendor?.companyName || q.vendor?.name;
                                    return (
                                        <th key={q.id} className="vendor-col">
                                            <div className="vendor-header">
                                                <span className="vendor-name">{vName}</span>
                                                <span className="text-xs text-slate-500 font-mono">{q.quotationNumber}</span>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {(compareData?.lineComparison || rfqItems || []).map((line, idx) => {
                                const itemName = line.itemName || line.catalogItem?.name || `Item ${idx + 1}`;
                                const itemQty = line.quantity || 1;
                                
                                return (
                                    <tr key={line.id || idx}>
                                        <td className="sticky-col">
                                            <div className="item-cell">
                                                <span className="item-code">{line.itemCode || line.catalogItem?.itemCode || `ITM-${idx + 1}`}</span>
                                                <span className="item-name">{itemName}</span>
                                            </div>
                                        </td>
                                        <td className="sticky-col qty-col font-semibold text-slate-700">
                                            {itemQty}
                                        </td>
                                        {rankedQuotations.map((q) => {
                                            // Find line in compareData or q.lines
                                            const quoteLine = q.lines?.find(l => l.rfqLineId === line.id || l.lineNumber === line.lineNumber);
                                            const uPrice = quoteLine ? parseFloat(quoteLine.unitPrice) : (parseFloat(q.totalAmount || 0) / (itemQty || 1));
                                            const tPrice = quoteLine ? parseFloat(quoteLine.totalPrice) : (uPrice * itemQty);

                                            return (
                                                <td key={q.id} className="price-col">
                                                    {uPrice > 0 ? (
                                                        <div className="price-cell">
                                                            <span className="unit-price">{formatCurrency(uPrice)}</span>
                                                            <span className="line-total">Subtotal: {formatCurrency(tPrice)}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="no-quote">—</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="total-row">
                                <td className="sticky-col" colSpan="2">
                                    <strong className="text-slate-800">Grand Total Penawaran</strong>
                                </td>
                                {rankedQuotations.map((q, idx) => (
                                    <td key={q.id} className={`total-cell ${idx === 0 ? 'lowest-total' : ''}`}>
                                        <strong className="text-base">{formatCurrency(q.totalAmount || q.total || 0)}</strong>
                                        {idx === 0 && <Badge variant="success" size="sm">Peringkat 1</Badge>}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* 3. MODAL INPUT & UPDATE SCORING */}
            <Modal
                isOpen={evalModalOpen}
                onClose={() => setEvalModalOpen(false)}
                title={`Formulir Evaluasi & Scoring Vendor`}
                size="md"
            >
                <form onSubmit={handleSaveEvaluation} className="space-y-4">
                    <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold">
                            <Building2 size={20} />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-sm">
                                {activeQuotation?.vendor?.companyName || activeQuotation?.vendor?.name}
                            </h4>
                            <p className="text-xs text-slate-500">
                                Nomor Penawaran: {activeQuotation?.quotationNumber}
                            </p>
                        </div>
                    </div>

                    {/* Technical Score */}
                    <div className="eval-slider-group">
                        <div className="eval-slider-header">
                            <span>1. Skor Teknis &amp; Spesifikasi (Bobot 40%)</span>
                            <span className="eval-slider-val">{technicalScore} / 100</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={technicalScore}
                            onChange={(e) => setTechnicalScore(e.target.value)}
                            className="eval-slider-input"
                        />
                        <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                            <span>0 (Tidak Sesuai)</span>
                            <span>50 (Memenuhi Minimal)</span>
                            <span>100 (Sangat Unggul)</span>
                        </div>
                    </div>

                    {/* Commercial Score */}
                    <div className="eval-slider-group">
                        <div className="eval-slider-header">
                            <span>2. Skor Komersial &amp; Harga (Bobot 40%)</span>
                            <span className="eval-slider-val">{commercialScore} / 100</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={commercialScore}
                            onChange={(e) => setCommercialScore(e.target.value)}
                            className="eval-slider-input"
                        />
                        <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                            <span>0 (Sangat Mahal)</span>
                            <span>50 (Rata-rata Pasar)</span>
                            <span>100 (Sangat Ekonomis)</span>
                        </div>
                    </div>

                    {/* Delivery Score */}
                    <div className="eval-slider-group">
                        <div className="eval-slider-header">
                            <span>3. Skor SLA &amp; Waktu Pengiriman (Bobot 20%)</span>
                            <span className="eval-slider-val">{deliveryScore} / 100</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={deliveryScore}
                            onChange={(e) => setDeliveryScore(e.target.value)}
                            className="eval-slider-input"
                        />
                        <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                            <span>0 (Lambat)</span>
                            <span>50 (Standar)</span>
                            <span>100 (Sangat Cepat)</span>
                        </div>
                    </div>

                    {/* Live Total Score Calculated */}
                    <div className="eval-live-score-box">
                        <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">
                            Kalkulasi Skor Akhir Terbobot (Overall Score)
                        </div>
                        <div className="eval-live-score-val">
                            {liveOverallScore}
                            <span className="text-sm font-semibold text-slate-400"> / 100</span>
                        </div>
                        <div className="eval-live-score-formula">
                            Formula: (40% × {technicalScore}) + (40% × {commercialScore}) + (20% × {deliveryScore})
                        </div>
                    </div>

                    {/* Notes */}
                    <TextArea
                        label="Catatan Evaluator &amp; Justifikasi Penilaian"
                        placeholder="Contoh: Spesifikasi perangkat sangat sesuai dengan TOR, garansi 16 bulan dan harga di bawah HPS..."
                        value={evaluationNotes}
                        onChange={(e) => setEvaluationNotes(e.target.value)}
                        rows={3}
                    />

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <Button variant="secondary" type="button" onClick={() => setEvalModalOpen(false)}>
                            Batal
                        </Button>
                        <Button type="submit" loading={evaluateMutation.isPending} icon={CheckCircle}>
                            Simpan Hasil Scoring
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

QuotationComparison.propTypes = {
    rfqId: PropTypes.string.isRequired,
    quotations: PropTypes.array,
    items: PropTypes.array
};
